"use client";

import Bugsnag, { type Event, type NotifiableError } from "@bugsnag/js";
import { getPlatform, getChannel } from "@/lib/app-context";

const API_KEY = process.env.NEXT_PUBLIC_BUGSNAG_API_KEY;
const BUILD_ID = process.env.NEXT_PUBLIC_BUILD_ID || "dev";
const RELEASE_STAGE = process.env.NODE_ENV === "production" ? "production" : "development";

let started = false;

function start() {
  if (started || !API_KEY || typeof window === "undefined") return;
  started = true;
  Bugsnag.start({
    apiKey: API_KEY,
    appVersion: BUILD_ID,
    releaseStage: RELEASE_STAGE,
    enabledReleaseStages: ["production", "development"],
    autoTrackSessions: false,
    // Re-evaluate platform/channel per event so a runtime change (e.g. Early
    // Access toggle reloading the renderer) is reflected in subsequent reports.
    onError: (event) => {
      event.addMetadata("app", { "Client Type": getPlatform(), Channel: getChannel() });
    },
  });
}

start();

type NotifyOptions = {
  context?: string;
  severity?: "error" | "warning" | "info";
  metadata?: Record<string, Record<string, unknown>>;
  user?: { id?: string; email?: string; name?: string };
};

export function notifyBugsnag(error: NotifiableError, opts: NotifyOptions = {}) {
  if (!API_KEY) return;
  start();
  try {
    Bugsnag.notify(error, (event: Event) => {
      if (opts.context) event.context = opts.context;
      if (opts.severity) event.severity = opts.severity;
      if (opts.user) event.setUser(opts.user.id, opts.user.email, opts.user.name);
      if (opts.metadata) {
        for (const [section, values] of Object.entries(opts.metadata)) {
          event.addMetadata(section, values);
        }
      }
    });
  } catch {
    // Never let error reporting throw.
  }
}

export function setBugsnagUser(user: { id?: string; email?: string; name?: string }) {
  if (!API_KEY) return;
  start();
  try {
    Bugsnag.setUser(user.id, user.email, user.name);
  } catch {
    // Ignore.
  }
}

export function leaveBugsnagBreadcrumb(
  message: string,
  metadata?: Record<string, unknown>,
  type: "navigation" | "request" | "process" | "log" | "user" | "state" | "error" | "manual" = "manual",
) {
  if (!API_KEY) return;
  start();
  try {
    Bugsnag.leaveBreadcrumb(message, metadata, type);
  } catch {
    // Ignore.
  }
}
